import React, { useLayoutEffect, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import cn from 'classnames';
import styles from './index.module.scss';
import FormHeader from '../Header/formHeader';
import { infoText } from 'utils/constants/index';
import { InfoPropTypes } from '../Tooltip';
import { t } from 'i18next';
import { makeStyles } from '@material-ui/styles';
import { Box } from '@material-ui/core';
import { Persist } from 'formik-persist';
import { FORM } from 'utils/constants/css';

export const HEADER_HEIGHT = 32;
const useStyles = makeStyles(theme => ({
  formBodyBackground: {
    backgroundColor: theme.palette.background.default,
    height: '-webkit-fill-available',
    overflow: 'auto',
    padding: '10px',
    paddingBottom: props => (props.noPadding ? 0 : FORM.PADDING_BOTTOM),
    '&::-webkit-scrollbar': {
      width: '0.7em',
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      backgroundColor: theme.palette.primary.main,
    },
    '& ::-webkit-scrollbar-corner': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  size: {
    height: '100vh',
    width: '100%',
  },
  sizePortal: {
    height: '85vh',
    width: '60vw',
  },
  noteBackground: {
    background: theme.palette.background.paper,
  },
}));
const FormContainer = ({
  size,
  title,
  onClickClose,
  initialValues = {},
  onSubmit,
  validationSchema,
  remarks,
  children,
  onChange,
  requireHeaderSaveBtn,
  enableReinitialize,
  formInfo,
  saveHoverText,
  note,
  resetForm,
  setResetForm,
  resetValues,
  hideFormHeader,
  formBodyClassname,
  useSendIcon = false,
  persistentStateName,
  disablePresist = false,
  noPadding = false, // adds padding to the bottom of the form
}) => {
  const [dimensions, setDimensions] = useState(null);
  const [targetRef, setTargetRef] = useState(null);
  const classes = useStyles({ noPadding });
  useLayoutEffect(() => {
    if (targetRef && typeof targetRef.getBoundingClientRect === 'function') {
      setDimensions(targetRef.getBoundingClientRect());
    }
  }, [targetRef]);

  const setRefValue = value => {
    if (value && value.current !== null) {
      setTargetRef(value);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize={enableReinitialize}
      validateOnChange={false}
      validateOnMount={false}
      validateOnBlur={false}
    >
      {props => {
        /* eslint multiline-comment-style: ["error", "starred-block"] */

        /*
         * Warning: Cannot update a component * while rendering a different component *
         * where asterisks are component names. you should never call setters/props like onChange while rendering. That will lead to a warning message.
         */
        useEffect(() => {
          onChange && onChange(props.values);
        }, [onChange, props?.values]);

        if (resetForm) {
          props.resetForm({ values: resetValues });
          setResetForm(false);
        }
        return (
          <div
            className={cn(
              { [classes.size]: !size },
              { [styles[size]]: size },
              { [classes.sizePortal]: size === 'portal' },
              styles.formContainer,
            )}
          >
            {!hideFormHeader && (
              <FormHeader
                title={t(title)}
                icon={true}
                onClickClose={onClickClose}
                onClickSave={props.handleSubmit}
                requireHeaderSaveBtn={requireHeaderSaveBtn}
                isformDirty={props.dirty}
                formInfo={formInfo}
                saveHoverText={t(saveHoverText)}
                useSendIcon={useSendIcon}
              />
            )}
            {note && (
              <div
                ref={setRefValue}
                className={cn(styles.note, classes.noteBackground)}
              >
                <span className={styles.infoIcon}>{t(infoText)}</span>
                <span className={styles.infoText}>{t(note)}</span>
              </div>
            )}
            <section
              className={cn(formBodyClassname, classes.formBodyBackground)}
              {...(dimensions &&
                dimensions.height && {
                  style: {
                    height: `calc(100% - (${dimensions.height}px + ${HEADER_HEIGHT}px))`,
                  },
                })}
            >
              <form className={styles.form}>
                <section
                  className={cn(styles.formBody, classes.formBodyBackground)}
                >
                  {children(props)}
                  <Box className={cn(styles.remarks)}>{`*${t(remarks)}`}</Box>
                </section>
              </form>
            </section>
            {!disablePresist && (
              <Persist
                isSessionStorage={true}
                name={`persistent-${persistentStateName}`}
              />
            )}
          </div>
        );
      }}
    </Formik>
  );
};

FormContainer.propTypes = {
  onChange: PropTypes.func,
  title: PropTypes.string,
  onClickClose: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
  validationSchema: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  remarks: PropTypes.string,
  isValid: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  values: PropTypes.object,
  size: PropTypes.oneOf(['s', 'sm', 'm', 'l', 'auto']),
  dirty: PropTypes.bool,
  requireHeaderSaveBtn: PropTypes.bool,
  enableReinitialize: PropTypes.bool,
  formInfo: InfoPropTypes,
  saveHoverText: PropTypes.string,
  note: PropTypes.string,
  resetForm: PropTypes.bool,
  setResetForm: PropTypes.func,
  resetValues: PropTypes.object,
  hideFormHeader: PropTypes.bool,
  formBodyClassname: PropTypes.string,
  persistentStateName: PropTypes.string,
  useSendIcon: PropTypes.bool,
  disablePresist: PropTypes.bool,
  noPadding: PropTypes.bool,
};

FormContainer.defaultProps = {
  requireHeaderSaveBtn: true,
};

export default FormContainer;
